// Bramka „Kancelaria nie ma jeszcze klucza szyfrującego".
//
// STAN, KTÓREGO DOTĄD NIC NIE OBSŁUGIWAŁO: firma ISTNIEJE (`hasCompany === true`, więc
// NoCompanyGate się nie pokazuje), ale nie ma ani jednej generacji klucza
// (`Workspaces.ActiveWorkspaceKeyId IS NULL`). Dotyczy każdej firmy założonej ZANIM wszedł
// model kluczy firmowych. Bez tej bramki użytkownik wchodzi do pełnego interfejsu, w którym
// każdy zapis odbija się o `CompanyKeyMissingError`, a treść nie odszyfrowuje się w ogóle —
// i nic nie tłumaczy dlaczego.
//
// Klucz generuje WYŁĄCZNIE przeglądarka (I4 + rozstrzygnięcie sekcji 2 dokumentu: klucz
// z BE nie ma podklucza szyfrującego, więc KeyIdHex nie zgadzałby się z identyfikatorem
// z pakietu PKESK). Na serwer idzie klucz publiczny oraz JEDEN grant — klucz prywatny
// owinięty kluczem pośrednim zakładającego. Pozostali członkowie dostają swoje granty
// osobno, przez „wydanie klucza" (GET /workspace/members/awaiting-key).

import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";

import Button from "components/Button";
import companyKeysService from "api/companyKeysService";
import CompanyContext from "contexts/CompanyContext";
import { EPrivileges } from "dto/EPrivileges";
import { buildCompanyKeyPayload } from "utils/buildCompanyKeyPayload";
import { getKeys } from "utils/getKeys";
import { getUserId } from "utils/getUserId";

import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

const CompanyKeyBootstrapGate: FC<Props> = ({ className = "" }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { company, hasPrivilege } = useContext(CompanyContext);
  const { mutateAsync: createCompanyKey } =
    companyKeysService.useCreateCompanyKey();

  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canGenerate = hasPrivilege(EPrivileges.CanGrantCompanyKeys);

  const generate = async () => {
    setWorking(true);
    setError(null);

    try {
      const userId = getUserId(auth.user!);
      const myKeys = await getKeys(userId!);

      if (!myKeys?.intermidatePrivateKey) {
        // Bez klucza pośredniego nie ma czym owinąć klucza firmy — to stan do odzyskiwania
        // dostępu (/recovery), nie do naprawienia tutaj.
        throw new Error(
          t("company.keyBootstrap.noLocalKeys", {
            defaultValue:
              "Ta przeglądarka nie ma Twoich kluczy. Odzyskaj dostęp i spróbuj ponownie.",
          }),
        );
      }

      await createCompanyKey(await buildCompanyKeyPayload(userId!, myKeys));

      // Klucz firmy jest odtwarzany z grantu przy starcie sesji (efekt w App.tsx), a mirror
      // krypto żyje tylko w pamięci — najprościej i najpewniej wejść w aplikację od nowa,
      // niż ręcznie odświeżać kilka niezależnych stanów.
      window.location.replace("/");
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : t("company.keyBootstrap.failed", {
              defaultValue:
                "Nie udało się utworzyć klucza kancelarii. Spróbuj ponownie.",
            }),
      );
      setWorking(false);
    }
  };

  return (
    <div className={`${styles.wrapper} ${className}`}>
      <div className={styles.card}>
        <div className={styles.title}>
          {t("company.keyBootstrap.title", {
            defaultValue: "Kancelaria nie ma jeszcze klucza szyfrującego",
          })}
        </div>

        <div className={styles.description}>
          {canGenerate
            ? t("company.keyBootstrap.descriptionOwner", {
                defaultValue:
                  "Dokumenty i korespondencja kancelarii są szyfrowane jej kluczem. " +
                  "Klucz powstaje w Twojej przeglądarce — serwer nigdy nie pozna jego części " +
                  "prywatnej. Do czasu jego utworzenia nic nie da się zapisać ani odczytać.",
              })
            : t("company.keyBootstrap.descriptionMember", {
                defaultValue:
                  "Dokumenty i korespondencja kancelarii są szyfrowane jej kluczem, a ten " +
                  "nie został jeszcze utworzony. Może to zrobić osoba zarządzająca kluczami " +
                  "w kancelarii — do tego czasu nic nie da się zapisać ani odczytać.",
              })}
        </div>

        {company?.Name && (
          <div className={styles.hint}>
            {t("company.keyBootstrap.company", {
              defaultValue: "Kancelaria: {{name}}",
              name: company.Name,
            })}
          </div>
        )}

        {error && <div className={styles.error}>{error}</div>}

        {canGenerate && (
          <div className={styles.buttons}>
            <Button
              onClick={generate}
              disabled={working}
              text={
                working
                  ? t("company.keyBootstrap.working", {
                      defaultValue: "Tworzenie klucza…",
                    })
                  : t("company.keyBootstrap.create", {
                      defaultValue: "Utwórz klucz kancelarii",
                    })
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyKeyBootstrapGate;
